import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';
import type {
  Meeting,
  MeetingDraft,
  MeetingFilters,
  MeetingPatch,
  PaginatedMeetings,
  UserMinimal,
} from '../types';

const BASE = '/meetings';

const toResults = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in data) {
    return (data as { results?: T[] }).results ?? [];
  }
  return [];
};

export const listMeetings = async (
  projectId: number,
  filters: MeetingFilters = {},
): Promise<PaginatedMeetings> => {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (filters.status) params.set('status', filters.status);
  if (filters.meeting_type) params.set('meeting_type', filters.meeting_type);
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.page_size) params.set('page_size', String(filters.page_size));

  const response = await GET(`${BASE}/meetings/?${params.toString()}`);
  const data = response.data;

  if (data && typeof data === 'object' && 'results' in data) {
    return data as PaginatedMeetings;
  }
  // Fallback if pagination is ever disabled.
  const results = Array.isArray(data) ? data : [];
  return {
    count: results.length,
    next: null,
    previous: null,
    results,
  };
};

export const getMeeting = async (id: string): Promise<Meeting> => {
  const response = await GET(`${BASE}/meetings/${id}/`);
  return response.data as Meeting;
};

export const createMeeting = async (draft: MeetingDraft): Promise<Meeting> => {
  const response = await POST(`${BASE}/meetings/`, draft);
  return response.data as Meeting;
};

export const updateMeeting = async (
  id: string,
  patch: MeetingPatch,
): Promise<Meeting> => {
  const response = await PATCH(`${BASE}/meetings/${id}/`, patch);
  return response.data as Meeting;
};

export const deleteMeeting = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/meetings/${id}/`);
};

/**
 * Reuses the project shareable users endpoint that other features (RFIs,
 * punch lists, etc.) use to populate user pickers.
 */
export const getProjectShareableUsers = async (
  projectId: number,
): Promise<UserMinimal[]> => {
  const response = await GET(`/base/projects/${projectId}/shareable-users/`);
  if (Array.isArray(response.data)) return response.data as UserMinimal[];
  if (response.data?.users) return response.data.users as UserMinimal[];
  return toResults<UserMinimal>(response.data);
};
